import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PageHeader, CtaBand } from "../components/site/Sections";
import { Reveal, MaskLines } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { TEAM, VALUES } from "../data/site";

const ParallaxImage = ({ src, alt }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className="relative aspect-[16/10] overflow-hidden rounded-[2rem] bg-muted sm:aspect-[21/9]">
      <motion.img style={{ y, scale: 1.18 }} src={src} alt={alt} loading="lazy" className="h-full w-full object-cover" />
    </div>
  );
};

export default function About() {
  return (
    <>
      <Seo path="/about" title="About us" description="Meet The Catalyst, a Canberra marketing agency built on plain talking, local knowledge and measurable results." />
      <PageHeader eyebrow="About The Catalyst" lines={["Small team.", "Big momentum."]}
        intro="We spent years in the engine room of other agencies, delivering big promises on bigger deadlines. Now we run our own show, and we like it here." />
      <div className="wrap"><Reveal><ParallaxImage src="/img/office.webp" alt="The Catalyst team working together in the studio" /></Reveal></div>

      <section className="wrap section grid gap-10 lg:grid-cols-12">
        <Reveal className="lg:col-span-4"><span className="eyebrow">Our story</span></Reveal>
        <div className="space-y-6 lg:col-span-8">
          <MaskLines as="h2" onLoad={false} lines={["From the back room", "to the front door."]} className="font-display text-3xl font-bold leading-[1.08] tracking-tight sm:text-5xl" />
          <Reveal delay={0.15}><p className="lede">For years we were the people behind the people. The back end of marketing agencies, burning the midnight oil and delivering what had been promised on timelines that were, let's say, optimistic. Somewhere between the late nights and the last-minute briefs, we noticed something: the part we loved most was the problem itself. Finding the most creative way to solve it, which more often than not turned out to be the most effective way too.</p></Reveal>
          <Reveal delay={0.25}><p className="lede">So we started The Catalyst to run our own show, keep improving and grow alongside the brands we work with. We love advertising for what it can do: shape culture, give a brand a real voice and personality, and hit business goals while doing some good for the community along the way.</p></Reveal>
          <Reveal delay={0.35}><p className="lede">In all honesty, it's an experiment. A deliberate one. Culture and community now change things faster than an F1 world champion changes gears, and we're here to help brands keep up, find their growth, and maybe even enjoy the ride.</p></Reveal>
        </div>
      </section>

      <section className="bg-ink py-20 text-canvas sm:py-28" aria-labelledby="values-heading">
        <div className="wrap">
          <Reveal><span className="eyebrow !text-neutral-300">What we value</span></Reveal>
          <MaskLines as="h2" onLoad={false} lines={["Four rules we keep."]} className="h2 mt-5" testId="values-heading" />
          <div className="mt-14 grid gap-px overflow-hidden rounded-[1.5rem] bg-canvas/10 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08} className="h-full bg-ink">
                <div className="h-full p-8" data-testid={`value-${i}`}>
                  <span className="font-display text-sm font-semibold text-flame">0{i + 1}</span>
                  <h3 className="mt-10 font-display text-2xl font-semibold tracking-tight">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-300">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="wrap section" aria-labelledby="team-heading">
        <Reveal><span className="eyebrow">The team</span></Reveal>
        <MaskLines as="h2" onLoad={false} lines={["The people", "you'll actually talk to."]} className="h2 mt-5" testId="team-heading" />
        <div className="mt-14 grid max-w-3xl grid-cols-1 gap-8 sm:grid-cols-2">
          {TEAM.map((t, i) => (
            <Reveal key={i} delay={i * 0.08} className={i % 2 ? "sm:mt-16" : ""}>
              <figure className="group" data-testid={`team-member-${i}`}>
                <div className="relative aspect-[4/5] overflow-hidden rounded-[1.5rem] bg-muted">
                  {t.image ? (
                    <img src={t.image} alt={`${t.name}, ${t.role}`} loading="lazy" className="h-full w-full object-cover grayscale transition-[filter,transform] duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                  ) : (
                    <div className={`grain relative flex h-full w-full items-end p-8 ${i % 2 ? "bg-flame" : "bg-navy"}`}>
                      <span className="font-display text-[9rem] font-extrabold leading-none tracking-tighter text-white/90 transition-transform duration-700 group-hover:-translate-y-2 sm:text-[11rem]" aria-hidden="true">{t.name[0]}</span>
                      <span className="absolute right-6 top-6 h-14 w-14 rounded-full border-[10px] border-white/20" aria-hidden="true" />
                    </div>
                  )}
                </div>
                <figcaption className="mt-4">
                  <span className="block font-display text-2xl font-semibold text-ink">{t.name}</span>
                  <span className="text-sm text-ink/65">{t.role}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>
      <CtaBand title={["Let's make", "something happen."]} />
    </>
  );
}
